import { useEventListener } from 'usehooks-ts';
import { useSegment } from '@repo/poly-hooks/segment';
import type { OnfidoModes } from './onfido.types';

/**
 * https://www.npmjs.com/package/onfido-sdk-ui#tracked-events
 * 17/18 tracked events
 * Not tracked:
 *   - ID_DOCUMENT_COUNTRY_SELECT - User reached the "Select issuing country" screen where the appropriate issuing country can be searched for and selected if supported
 */
export const ONFIDO_EVENTS_MAP = {
  WELCOME: 'welcome_page_loaded',
  DOCUMENT_TYPE_SELECT: 'user_reached_onfido_flow',
  DOCUMENT_CAPTURE_FRONT: 'poi_type_selected',
  DOCUMENT_CAPTURE_BACK: 'poi_front_side_confirmed',
  DOCUMENT_CAPTURE_CONFIRMATION_FRONT: 'poi_front_side_uploaded',
  DOCUMENT_CAPTURE_CONFIRMATION_BACK: 'poi_back_side_uploaded',
  FACIAL_INTRO: 'selfie_intro_page_loaded',
  FACIAL_CAPTURE: 'selfie_capture_screen',
  FACIAL_CAPTURE_CONFIRMATION: 'selfie_taken',
  VIDEO_FACIAL_INTRO: 'liveness_intro_page_loaded',
  VIDEO_FACIAL_CAPTURE_STEP_1: 'liveness_started',
  VIDEO_FACIAL_CAPTURE_STEP_2: 'liveness_first_challenge_performed',
  UPLOAD: 'document_confirmed',
  USER_CONSENT: 'user_consent_screen_reached',
  CROSS_DEVICE_INTRO: 'continue_on_your_phone_intro_screen_reached',
  CROSS_DEVICE_GET_LINK: 'get_your_secure_link_screen_reached',
  CROSS_DEVICE_START: 'document_capture_screen_reached_on_mobile_after_visiting_cross_device_link',
};

const EVENTS_NOT_TRACKED = {
  onboarding_onfido: [ONFIDO_EVENTS_MAP.WELCOME, ONFIDO_EVENTS_MAP.USER_CONSENT],
  onfido_standalone: null,
} satisfies Record<OnfidoModes, string[] | null>;

export type OnfidoEvent = keyof typeof ONFIDO_EVENTS_MAP;

declare global {
  interface WindowEventMap {
    userAnalyticsEvent: CustomEvent;
  }
}

export function useOnfidoEvents(
  mode: OnfidoModes,
  onEvent: (event: keyof typeof ONFIDO_EVENTS_MAP) => void
): void {
  const segment = useSegment();

  const onUserAnalyticsEvent = (
    event: CustomEvent<{ eventName: keyof typeof ONFIDO_EVENTS_MAP }>
  ): void => {
    const eventName = event.detail.eventName;
    const segmentEvent = ONFIDO_EVENTS_MAP[eventName];

    if (EVENTS_NOT_TRACKED[mode]?.includes(segmentEvent)) {
      return;
    }

    segment.track(`${mode}_${segmentEvent}`);

    onEvent(eventName);
  };

  useEventListener<'userAnalyticsEvent'>('userAnalyticsEvent', onUserAnalyticsEvent);
}
